import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { _baseRequest, COMMON } from '../../utils/baseApi';
import { getToken } from '../../utils/localStorage';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  entityType?: string;
  entityId?: string;
}

interface ChatState {
  messages: ChatMessage[];
  history: ChatMessage[];
  historyLoading: boolean;
  historyPage: number;
  historyHasMore: boolean;
  isLoading: boolean;
  streamStatus: string;
  contextTitle: string | null;
  lastDoneResponse: string | null;
}

const initialState: ChatState = {
  messages: [],
  history: [],
  historyLoading: false,
  historyPage: 0,
  historyHasMore: true,
  isLoading: false,
  streamStatus: '',
  contextTitle: null,
  lastDoneResponse: null,
};

const CHAT_HISTORY_URL = '/v1/chatbot/history';
const CHAT_STREAM_URL = `${COMMON.apiBaseUrl()}/v1/chatbot/chat/stream`;

let idCounter = 0;
const genId = () => `msg-${Date.now()}-${++idCounter}`;

interface HistoryResponse {
  messages: ChatMessage[];
  hasMore: boolean;
}

const getUserQuery = (m: any): string => {
    const raw =
      m.query ??
      m.question ??
      m.userMessage ??
      m.user_message ??
      m.user_query ??
      m.message ??
      m.prompt ??
      m.text ??
      '';
    return typeof raw === 'string' ? raw : '';
  };

  const normalizeHistory = (payload: any): HistoryResponse => {
    const root = payload?.data ?? payload ?? {};
    const list = root.data ?? root.messages ?? root;
    const rawItems = Array.isArray(list) ? list : [];

    // API returns newest -> oldest. Iterate in reverse so we build a
    // chronological oldest -> newest list, with each conversation pair
    // kept in the natural order: question first, then answer.
    const chronological = [...rawItems].reverse();

    const messages: ChatMessage[] = [];
    chronological.forEach((m: any, idx: number) => {
      const id = m.id?.toString();
      const ts = m.created_at
        ? new Date(m.created_at).getTime()
        : Date.now();
      const cleanQuery = getUserQuery(m)
        .replace(/\n\n\[Active Context:[\s\S]*\]/, '')
        .trim();
      if (cleanQuery) {
        messages.push({
          id: id ? `${id}-q` : `hist-${ts}-q`,
          role: 'user',
          content: cleanQuery,
        });
      }
      if (m.response !== undefined && m.response !== null && m.response !== '') {
        messages.push({
          id: id ? `${id}-a` : `hist-${ts}-a`,
          role: 'assistant',
          content: m.response,
          entityType: m.entity_type,
          entityId: m.entity_id,
        });
      } else if (m.content) {
        messages.push({
          id: id || `hist-${ts}-${idx}`,
          role: m.role || 'assistant',
          content: m.content,
        });
      }
    });

    const hasMore =
      root.hasNextPage ??
      root.hasMore ??
      payload?.data?.hasNextPage ??
      payload?.data?.hasMore ??
      rawItems.length > 0;
    return { messages, hasMore };
  };

export const fetchChatHistory = createAsyncThunk(
  'chat/fetchChatHistory',
  async ({ limit, page,entityId,entityType }: { limit: number; page: number,entityId:any,entityType:any }, { rejectWithValue }) => {
    try {
      const url = `${CHAT_HISTORY_URL}?limit=${limit}&page=${page}&entityId=${entityId}&entityType=${entityType}`;
      const payload = await _baseRequest(url, 'GET');
      return { ...normalizeHistory(payload), page };
    } catch (err: any) {
      return rejectWithValue(err?.message || 'Failed to load history');
    }
  },
);

export const streamBotResponse = createAsyncThunk(
  'chat/streamBotResponse',
  async (payload: any[], { dispatch, rejectWithValue }) => {
    const assistantId = genId();
    dispatch(
      chatSlice.actions.addBotMessage({
        id: assistantId,
        role: 'assistant',
        content: '',
      }),
    );
    dispatch(setStreamStatus('Thinking...'));

    try {
      const token = getToken();
      const response = await fetch(CHAT_STREAM_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok || !response.body) {
        throw new Error(`Stream failed: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let contentAcc = '';
      let buffer = '';

      const handleEvent = (data: string) => {
        if (!data || data.startsWith(':')) return;
        let jsonStr = data;
        if (data.startsWith('data:')) {
          jsonStr = data.slice(5).trim();
        }
        if (!jsonStr) return;
        try {
          const evt = JSON.parse(jsonStr);
          if (evt.type === 'status' && evt.message) {
            dispatch(setStreamStatus(evt.message));
            dispatch(setChatLoading(true));
          } else if (evt.type === 'chunk' && evt.content) {
            contentAcc += evt.content;
            dispatch(
              chatSlice.actions.updateBotMessage({
                id: assistantId,
                content: contentAcc,
              }),
            );
            dispatch(setStreamStatus(''));
          } else if (evt.type === 'done') {
            const finalContent =
              typeof evt.response === 'string' ? evt.response : contentAcc;
            contentAcc = finalContent;
            dispatch(
              chatSlice.actions.updateBotMessage({
                id: assistantId,
                content: finalContent,
              }),
            );
            dispatch(setStreamDoneResponse(finalContent));
            dispatch(setStreamStatus(''));
          }
        } catch {
          // not JSON — treat as raw text chunk
          contentAcc += jsonStr;
          dispatch(
            chatSlice.actions.updateBotMessage({
              id: assistantId,
              content: contentAcc,
            }),
          );
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let sepIdx: number;
        while ((sepIdx = buffer.indexOf('\n\n')) !== -1) {
          const rawEvent = buffer.slice(0, sepIdx);
          buffer = buffer.slice(sepIdx + 2);
          for (const line of rawEvent.split('\n')) {
            handleEvent(line.trim());
          }
        }
      }
      // flush any trailing buffered event
      if (buffer.trim()) {
        for (const line of buffer.split('\n')) {
          handleEvent(line.trim());
        }
      }

      dispatch(setStreamStatus(''));
      return { id: assistantId, content: contentAcc };
    } catch (err: any) {
      dispatch(
        chatSlice.actions.updateBotMessage({
          id: assistantId,
          content: accFallback(err),
        }),
      );
      dispatch(setStreamStatus(''));
      return rejectWithValue(err?.message || 'Stream error');
    }
  },
);

const accFallback = (err: any) =>
  `Sorry, I couldn't reach the assistant right now. (${err?.message || 'error'})`;

export const addUserMessage = createAsyncThunk(
  'chat/addUserMessage',
  async (text: string) => ({ id: genId(), role: 'user' as const, content: text }),
);

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    addBotMessage(state, action: PayloadAction<ChatMessage>) {
      state.messages.push(action.payload);
      state.isLoading = true;
      state.lastDoneResponse = null;
    },
    updateBotMessage(
      state,
      action: PayloadAction<{ id: string; content: string }>,
    ) {
      const msg = state.messages.find((m) => m.id === action.payload.id);
      if (msg) msg.content = action.payload.content;
      if (action.payload.content) state.streamStatus = '';
    },
    setStreamStatus(state, action: PayloadAction<string>) {
      state.streamStatus = action.payload;
    },
    setStreamDoneResponse(state, action: PayloadAction<string | null>) {
      state.lastDoneResponse = action.payload;
    },
    setContextTitle(state, action: PayloadAction<string | null>) {
      state.contextTitle = action.payload;
    },
    setChatLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    clearChat(state) {
      state.messages = [];
      state.history = [];
      state.historyPage = 0;
      state.historyHasMore = true;
      state.isLoading = false;
      state.streamStatus = '';
    },
    resetChatHistory(state) {
      state.history = [];
      state.historyPage = 0;
      state.historyHasMore = true;
      state.historyLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addUserMessage.pending, (state, action) => {
        state.messages.push({
          id: genId(),
          role: 'user',
          content: action.meta.arg,
        });
      })
      .addCase(fetchChatHistory.pending, (state) => {
        state.historyLoading = true;
      })
      .addCase(fetchChatHistory.fulfilled, (state, action) => {
        state.historyLoading = false;
        state.historyPage = action.payload.page;
        state.historyHasMore = action.payload.hasMore;
        state.history = [...action.payload.messages, ...state.history];
      })
      .addCase(fetchChatHistory.rejected, (state) => {
        state.historyLoading = false;
        state.historyHasMore = false;
      })
      .addCase(streamBotResponse.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(streamBotResponse.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(streamBotResponse.rejected, (state) => {
        state.isLoading = false;
      });
  },
});

export const {
  setContextTitle,
  setStreamStatus,
  setStreamDoneResponse,
  setChatLoading,
  clearChat,
  resetChatHistory,
} = chatSlice.actions;

export default chatSlice.reducer;